import type { EntityDB } from "./db";
import {
  getVisibleContentVisibilities,
  type BaseEntity,
  type ContentVisibility,
  type SearchResult,
  type SearchOptions,
} from "./types";
import type { IEmbeddingService } from "./embedding-types";
import type { EntitySerializer } from "./entity-serializer";
import { type Logger } from "@brains/utils/logger";
import { z } from "@brains/utils/zod";
import { sql, and, desc, inArray, type SQL } from "drizzle-orm";
import { entities } from "./schema/entities";

export const MAX_SEARCH_QUERY_CHARS = 12_000;
const MAX_VECTOR_DISTANCE = 0.82;

export function prepareSearchQuery(
  query: string,
  logger?: Logger,
  maxChars: number = MAX_SEARCH_QUERY_CHARS,
): string {
  const normalizedQuery = query.trim().replace(/\s+/g, " ");

  if (normalizedQuery.length <= maxChars) {
    return normalizedQuery;
  }

  logger?.warn("Truncating search query that exceeds max length", {
    originalLength: normalizedQuery.length,
    truncatedLength: maxChars,
  });

  return normalizedQuery.slice(0, maxChars);
}

/**
 * Schema for search options (excluding tags)
 */
const searchOptionsSchema = z.object({
  limit: z.number().int().positive().optional().default(20),
  offset: z.number().int().min(0).optional().default(0),
  types: z.array(z.string()).optional().default([]),
  excludeTypes: z.array(z.string()).optional().default([]),
  weight: z.record(z.string(), z.number()).optional(),
  visibilityScope: z.enum(["public", "shared", "restricted"]).optional(),
  includeUngenerated: z.boolean().optional().default(false),
  minScore: z.number().min(0).optional(),
});

const entityMetadataSchema = z.preprocess(
  (value) => {
    if (typeof value !== "string") return value;
    return JSON.parse(value);
  },
  z.record(z.string(), z.unknown()),
);

/**
 * EntitySearch handles all search operations for entities
 * Extracted from EntityService for single responsibility
 */
export class EntitySearch {
  private db: EntityDB;
  private embeddingService: IEmbeddingService;
  private serializer: EntitySerializer;
  private logger: Logger;

  constructor(
    db: EntityDB,
    embeddingService: IEmbeddingService,
    serializer: EntitySerializer,
    logger: Logger,
  ) {
    this.db = db;
    this.embeddingService = embeddingService;
    this.serializer = serializer;
    this.logger = logger.child("EntitySearch");
  }

  /**
   * Search entities by query using vector similarity
   */
  public async search<T extends BaseEntity = BaseEntity>(
    query: string,
    options?: SearchOptions,
  ): Promise<SearchResult<T>[]> {
    const validatedOptions = searchOptionsSchema.parse(options ?? {});
    const {
      limit,
      offset,
      types,
      excludeTypes,
      weight,
      visibilityScope,
      includeUngenerated,
      minScore,
    } = validatedOptions;

    // Check if we have weights to apply
    const hasWeights = weight && Object.keys(weight).length > 0;
    const preparedQuery = prepareSearchQuery(query, this.logger);

    this.logger.debug(
      `Searching entities with query (${preparedQuery.length} chars)`,
    );

    // Generate embedding for the query
    const { embedding: queryEmbedding } =
      await this.embeddingService.generateEmbedding(preparedQuery);

    // Convert Float32Array to JSON array for SQL
    const embeddingArray = JSON.stringify(Array.from(queryEmbedding));

    const weightMultiplier = this.buildWeightMultiplier(
      hasWeights ? weight : undefined,
    );

    // Build type filter conditions for drizzle
    const typeConditions: SQL[] = [];
    if (types.length > 0) {
      typeConditions.push(
        sql`${entities.entityType} IN (${sql.join(
          types.map((t) => sql`${t}`),
          sql`, `,
        )})`,
      );
    }
    if (excludeTypes.length > 0) {
      typeConditions.push(
        sql`${entities.entityType} NOT IN (${sql.join(
          excludeTypes.map((t) => sql`${t}`),
          sql`, `,
        )})`,
      );
    }

    return this.searchWithAttachedDb<T>(
      embeddingArray,
      weightMultiplier,
      [
        ...typeConditions,
        ...this.buildVisibilityConditions(visibilityScope),
        ...this.buildGenerationStatusConditions(includeUngenerated),
      ],
      limit,
      offset,
      preparedQuery,
      minScore,
    );
  }

  private buildVisibilityConditions(
    visibilityScope?: ContentVisibility,
  ): SQL[] {
    // Fail closed: undefined scope filters to public-only.
    const scope: ContentVisibility = visibilityScope ?? "public";
    if (scope === "restricted") {
      return [];
    }
    return [inArray(entities.visibility, getVisibleContentVisibilities(scope))];
  }

  private buildGenerationStatusConditions(includeUngenerated: boolean): SQL[] {
    if (includeUngenerated) return [];
    return [
      sql`(json_extract(${entities.metadata}, '$.status') IS NULL OR json_extract(${entities.metadata}, '$.status') NOT IN ('generating', 'failed'))`,
    ];
  }

  /**
   * FTS5 boost weight. When a keyword match is found, this fraction of the
   * final score comes from FTS5 rank, the rest from vector similarity.
   * 0.3 = 30% keyword, 70% semantic.
   */
  private static readonly FTS_ALPHA = 0.3;

  /**
   * Build a parameterized CASE expression for entity-type score multipliers.
   * Weight keys may be caller-provided, so avoid raw SQL string interpolation.
   */
  private buildWeightMultiplier(weight?: Record<string, number>): SQL {
    const entries = Object.entries(weight ?? {}).filter(([, multiplier]) =>
      Number.isFinite(multiplier),
    );

    if (entries.length === 0) {
      return sql`1.0`;
    }

    const cases = entries.map(
      ([entityType, multiplier]) =>
        sql`WHEN ${entities.entityType} = ${entityType} THEN ${multiplier}`,
    );

    return sql`CASE ${sql.join(cases, sql` `)} ELSE 1.0 END`;
  }

  /**
   * Execute search against an attached embedding database (aliased as "emb").
   */
  private async searchWithAttachedDb<T extends BaseEntity = BaseEntity>(
    embeddingArray: string,
    weightMultiplier: SQL,
    typeConditions: SQL[],
    limit: number,
    offset: number,
    query: string,
    minScore: number | undefined,
  ): Promise<SearchResult<T>[]> {
    const alpha = EntitySearch.FTS_ALPHA;

    // Vector similarity score (0..1, higher is better)
    const vectorScore = sql<number>`(1.0 - vector_distance_cos(emb_e.embedding, vector32(${embeddingArray})) / 2.0) * (${weightMultiplier})`;
    const distanceExpr = sql<number>`vector_distance_cos(emb_e.embedding, vector32(${embeddingArray}))`;

    // FTS5 keyword boost via subquery: 1.0 when matched, 0.0 when not.
    // Wrap in double quotes for phrase matching — prevents special characters
    // (?, *, OR, AND, etc.) from being parsed as FTS5 operators.
    const ftsQuery = '"' + query.replace(/"/g, '""') + '"';
    const ftsBoost = sql<number>`CASE WHEN EXISTS (
      SELECT 1 FROM entity_fts WHERE entity_fts MATCH ${ftsQuery}
        AND entity_id = ${entities.id} AND entity_type = ${entities.entityType}
    ) THEN 1.0 ELSE 0.0 END`;

    // Combined score: (1-α)*vector + α*keyword_match
    const combinedScore = sql<number>`(${1 - alpha} * ${vectorScore}) + (${alpha} * ${ftsBoost})`;

    const results = await this.db
      .select({
        id: entities.id,
        entityType: entities.entityType,
        content: entities.content,
        contentHash: entities.contentHash,
        visibility: entities.visibility,
        created: entities.created,
        updated: entities.updated,
        metadata: entities.metadata,
        distance: distanceExpr,
        weighted_score: combinedScore,
      })
      .from(entities)
      .innerJoin(
        sql`emb.embeddings AS emb_e`,
        sql`${entities.id} = emb_e.entity_id AND ${entities.entityType} = emb_e.entity_type`,
      )
      .where(
        and(
          sql`${distanceExpr} < ${MAX_VECTOR_DISTANCE}`,
          ...(minScore !== undefined
            ? [sql`${combinedScore} >= ${minScore}`]
            : []),
          ...typeConditions,
        ),
      )
      .orderBy(desc(combinedScore))
      .limit(limit)
      .offset(offset);

    return this.mapSearchResults<T>(results, query);
  }

  /**
   * Search entities by type and query
   */
  public async searchEntities(
    entityType: string,
    query: string,
    options?: { limit?: number },
  ): Promise<SearchResult[]> {
    // Build search options with the entity type filter
    const searchOptions: SearchOptions = {
      types: [entityType],
      limit: options?.limit ?? 20,
      offset: 0,
      sortBy: "relevance",
      sortDirection: "desc",
    };

    return this.search(query, searchOptions);
  }

  /**
   * Return all embedded entities with their raw cosine distance to the query.
   * No threshold filter — used for diagnostics and threshold tuning.
   * Results sorted by distance ascending (closest first).
   */
  public async searchWithDistances(
    query: string,
  ): Promise<
    Array<{ entityId: string; entityType: string; distance: number }>
  > {
    const preparedQuery = prepareSearchQuery(query, this.logger);
    const { embedding: queryEmbedding } =
      await this.embeddingService.generateEmbedding(preparedQuery);
    const embeddingArray = JSON.stringify(Array.from(queryEmbedding));

    const distanceExpr = sql<number>`vector_distance_cos(emb_e.embedding, vector32(${embeddingArray}))`;

    const results = await this.db
      .select({
        entityId: entities.id,
        entityType: entities.entityType,
        distance: distanceExpr,
      })
      .from(entities)
      .innerJoin(
        sql`emb.embeddings AS emb_e`,
        sql`${entities.id} = emb_e.entity_id AND ${entities.entityType} = emb_e.entity_type`,
      )
      .orderBy(sql`${distanceExpr} ASC`);

    return results;
  }

  /**
   * Transform raw query rows into SearchResult objects
   */
  private mapSearchResults<T extends BaseEntity = BaseEntity>(
    results: Array<{
      id: string;
      entityType: string;
      content: string;
      contentHash: string;
      visibility: ContentVisibility;
      created: number;
      updated: number;
      metadata: unknown;
      weighted_score: number;
    }>,
    query: string,
  ): SearchResult<T>[] {
    const searchResults: SearchResult<T>[] = [];

    for (const row of results) {
      try {
        const metadata = entityMetadataSchema.parse(row.metadata);

        const entity = this.serializer.reconstructEntity<T>({
          id: row.id,
          entityType: row.entityType,
          content: row.content,
          contentHash: row.contentHash,
          visibility: row.visibility,
          created: row.created,
          updated: row.updated,
          metadata,
        });

        searchResults.push({
          entity,
          score: row.weighted_score,
          excerpt: this.createExcerpt(row.content, query),
        });
      } catch (error) {
        this.logger.error(`Failed to parse entity during search: ${error}`);
      }
    }

    const queryPreview =
      query.length > 50 ? query.substring(0, 50) + "..." : query;
    this.logger.debug(
      `Found ${searchResults.length} results for query "${queryPreview}"`,
    );

    return searchResults;
  }

  /**
   * Create an excerpt from content based on query
   */
  private createExcerpt(content: string, query: string): string {
    const maxLength = 200;
    const queryLower = query.toLowerCase();
    const contentLower = content.toLowerCase();

    // Find the position of the query in the content
    const position = contentLower.indexOf(queryLower);

    if (position !== -1) {
      // Extract text around the query
      const start = Math.max(0, position - 50);
      const end = Math.min(content.length, position + queryLower.length + 50);
      let excerpt = content.slice(start, end);

      // Add ellipsis if needed
      if (start > 0) excerpt = "..." + excerpt;
      if (end < content.length) excerpt = excerpt + "...";

      return excerpt;
    }

    // If query not found, return beginning of content
    return (
      content.slice(0, maxLength) + (content.length > maxLength ? "..." : "")
    );
  }
}
